/**
 * What a slow tap was, in words a report can read (#865).
 *
 * PostHog's web vitals carry an INP value and nothing else: the entry is
 * serialised as `{}`, so a 2 s tap on an iPhone said nothing about what was
 * tapped or what the main thread was doing. The browser's Event Timing API
 * gives each interaction its target, its input delay (the thread was busy
 * before the tap) and its processing time (the handler itself); the Long
 * Tasks API (Chrome) names the tasks. This module is the pure half: naming a
 * target, grouping the entries of one interaction, pairing a task with the
 * last tap. `mainThreadWatch.ts` owns the observers.
 */

/** The slice of a DOM element these helpers read; duck-typed for tests. */
export interface TargetLike {
  tagName?: string;
  getAttribute?: (name: string) => string | null;
  textContent?: string | null;
  parentElement?: TargetLike | null;
}

export interface TargetDescription {
  /** `data-testid` of the element or its nearest ancestor that has one. */
  testId?: string;
  /** A short label: the test id, an aria-label, a button's text, or the tag. */
  label: string;
}

const MAX_LABEL = 40;
const MAX_ANCESTORS = 8;
const PRESSABLE_TAGS = new Set(['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA']);

const attr = (el: TargetLike, name: string): string | null =>
  typeof el.getAttribute === 'function' ? el.getAttribute(name) : null;

/** A thing a finger presses: a button, a link, or RNW's Pressable (a div with a tabindex). */
function isPressable(el: TargetLike): boolean {
  const tag = (el.tagName ?? '').toUpperCase();
  if (PRESSABLE_TAGS.has(tag)) return true;
  const role = attr(el, 'role');
  if (role === 'button' || role === 'link' || role === 'tab' || role === 'menuitem') return true;
  return attr(el, 'tabindex') != null;
}

const trim = (s: string): string => {
  const one = s.replace(/\s+/g, ' ').trim();
  return one.length > MAX_LABEL ? `${one.slice(0, MAX_LABEL - 1)}…` : one;
};

/**
 * Name the element a tap landed on. The test id wins (every planner control
 * has one), then an aria-label, then the text of a pressable element when it
 * is short enough to be a label and not a paragraph; a plain tag otherwise.
 * Text is read only off pressables, so a tap on a note never reports the note.
 */
export function describeTarget(target: TargetLike | null | undefined): TargetDescription {
  let el: TargetLike | null | undefined = target;
  let testId: string | undefined;
  let pressable: TargetLike | undefined;
  let aria: string | undefined;
  for (let depth = 0; el && depth < MAX_ANCESTORS; depth += 1, el = el.parentElement) {
    if (!testId) {
      const id = attr(el, 'data-testid');
      if (id) testId = id;
    }
    if (!aria) {
      const a = attr(el, 'aria-label');
      if (a) aria = trim(a);
    }
    if (!pressable && isPressable(el)) pressable = el;
    if (testId) break;
  }
  if (testId) return { testId, label: testId };
  if (aria) return { label: aria };
  if (pressable) {
    const text = (pressable.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (text && text.length <= 60) return { label: trim(text) };
    return { label: (pressable.tagName ?? 'pressable').toLowerCase() };
  }
  return { label: (target?.tagName ?? 'unknown').toLowerCase() };
}

/** One Event Timing entry, the fields this module reads. */
export interface InteractionEntryLike {
  name: string;
  startTime: number;
  duration: number;
  processingStart: number;
  processingEnd: number;
  interactionId?: number;
  target?: TargetLike | null;
}

export interface InteractionSummary {
  interactionId: number;
  /** The event name of the longest entry (`pointerdown`, `click`, `keydown`…). */
  eventType: string;
  startTime: number;
  durationMs: number;
  inputDelayMs: number;
  processingMs: number;
  presentationMs: number;
  target: TargetLike | null | undefined;
}

/**
 * One tap is several entries (pointerdown, pointerup, click) sharing an
 * `interactionId`, as web-vitals groups them for INP. The interaction's
 * duration is its longest entry; the split (input delay, processing,
 * presentation) is that entry's. Entries with no interaction id (hover,
 * scroll) are not interactions.
 */
export function summarizeInteractions(entries: InteractionEntryLike[]): InteractionSummary[] {
  const byId = new Map<number, InteractionSummary>();
  for (const e of entries) {
    const id = e.interactionId ?? 0;
    if (!id) continue;
    const current = byId.get(id);
    if (current && current.durationMs >= e.duration) {
      current.startTime = Math.min(current.startTime, e.startTime);
      continue;
    }
    byId.set(id, {
      interactionId: id,
      eventType: e.name,
      startTime: Math.min(e.startTime, current?.startTime ?? e.startTime),
      durationMs: e.duration,
      inputDelayMs: Math.max(0, e.processingStart - e.startTime),
      processingMs: Math.max(0, e.processingEnd - e.processingStart),
      presentationMs: Math.max(0, e.startTime + e.duration - e.processingEnd),
      target: e.target ?? current?.target,
    });
  }
  return [...byId.values()];
}

export interface TapRecord {
  t: number;
  label: string;
  testId?: string;
}

/**
 * A tap is recorded when its listener runs, a few ms after the task that
 * dispatches it started; a tap up to this much after `t` still belongs to it.
 */
export const TAP_SLACK_MS = 50;

/** The last tap at or just after `t`, if it was within `withinMs` before it. */
export function tapBefore(taps: readonly TapRecord[], t: number, withinMs: number): TapRecord | undefined {
  let best: TapRecord | undefined;
  for (const tap of taps) {
    if (tap.t <= t + TAP_SLACK_MS && (!best || tap.t > best.t)) best = tap;
  }
  return best && t - best.t <= withinMs ? best : undefined;
}

/** A budget: at most `max` reports, so a stuck page cannot flood a session. */
export function makeBudget(max: number): () => boolean {
  let used = 0;
  return () => {
    if (used >= max) return false;
    used += 1;
    return true;
  };
}
